import { storageKey } from '@invite/app-core';

/**
 * The pending 2FA token and the state the configure/verify/recovery screens need. In memory with a
 * sessionStorage mirror so a reload during 2FA does not lose the flow.
 */
export interface TwoFactorConfig {
  isSmsFallback: boolean;
  phone?: string | null;
  qrUrl?: string | null;
  secretKey?: string | null;
  recoveryCodes?: string[];
}

export interface TwoFactorSession {
  token: string;
  returnUrl: string;
  verificationType?: 'app' | 'sms';
  phone?: string | null;
  config?: TwoFactorConfig;
  infoMessage?: string;
  /** The access token issued by verification, held until the recovery codes are acknowledged. */
  access?: { token: string; tokenExpiryDate: string };
}

const key = (): string => storageKey('2fa');
let current: TwoFactorSession | null = null;

const read = (): TwoFactorSession | null => {
  try {
    const raw = sessionStorage.getItem(key());
    return raw === null ? null : (JSON.parse(raw) as TwoFactorSession);
  } catch {
    return null;
  }
};

export const twoFactorSession = {
  get: (): TwoFactorSession | null => current ?? (current = read()),
  set: (session: TwoFactorSession): void => {
    current = session;
    try {
      sessionStorage.setItem(key(), JSON.stringify(session));
    } catch {
      // memory only
    }
  },
  update: (patch: Partial<TwoFactorSession>): void => {
    const base = twoFactorSession.get();
    if (base !== null) {
      twoFactorSession.set({ ...base, ...patch });
    }
  },
  clear: (): void => {
    current = null;
    try {
      sessionStorage.removeItem(key());
    } catch {
      // ignore
    }
  }
};
